<script setup lang="ts">
import { ref, computed } from "vue";
import JourneyLayout from "@/components/layout/JourneyLayout/JourneyLayout.vue";
import PolicySummary from "./PolicySummary/PolicySummary.vue";
import CustomerDetails from "./CustomerDetails/CustomerDetails.vue";
import PolicyDocumentation from "./PolicyDocumentation/PolicyDocumentation.vue";
import ConfirmationSection from "./ConfirmationSection/ConfirmationSection.vue";
import { PageEnum, SmokerStatusEnum } from "@/enums";
import { DocumentsOptionEnum, EventTypeEnum } from "@/models";
import { useCustomerStore, useDataLayerStore } from "@/stores";
import PageHeading from "@/components/general/PageHeading/PageHeading.vue";
import type { RadioOption } from "@/models";

const customer = useCustomerStore();
const dataLayer = useDataLayerStore();

const emit = defineEmits<{
    submitSection: [currentPage: PageEnum, nextPage: PageEnum];
}>();

const submitSection = (currentPage: PageEnum, whereTo: PageEnum) => {
    dataLayer.triggerEvent(EventTypeEnum.PageInteraction, "button|Buy Now");
    emit("submitSection", currentPage, whereTo);
};

const viewTerms = () => {
    dataLayer.triggerEvent(
        EventTypeEnum.PageInteraction,
        "download|policy Ts&Cs"
    );
};

const policyOptions: RadioOption[] = [
    {
        key: "Online",
        label: "Yes, I want to access my policy documents 24/7 online",
        value: DocumentsOptionEnum.Online,
    },
    {
        key: "Paper",
        label: "I want paper policy documents",
        value: DocumentsOptionEnum.Paper,
    },
];

const smoker = computed((): "Non smoker" | "Smoker" =>
    customer.smoker !== SmokerStatusEnum.NoneAtAll ? "Smoker" : "Non smoker"
);
const selectedOption = ref(policyOptions[0].value);

const updateSelectedOption = (value: string) => {
    customer.documentsOption = value as DocumentsOptionEnum;
};
</script>

<template>
    <JourneyLayout>
        <template #main>
            <form :id="PageEnum.YourPlan">
                <div>
                    <div class="your-plan-page">
                        <PageHeading>Cover Summary</PageHeading>
                        <PolicySummary />
                        <CustomerDetails
                            :name="`${customer?.title ?? ''} ${customer?.firstname} ${customer?.middlename} ${customer?.surname}`"
                            :date-of-birth="customer?.longDob ?? ''"
                            :address="customer.address"
                            :phone="customer?.dayPhone ?? ''"
                            :email="customer.email"
                            :smoker="smoker"
                        />
                        <PolicyDocumentation
                            v-model:selected-option="selectedOption"
                            :options="policyOptions"
                            @update:selected-option="updateSelectedOption"
                        />
                        <ConfirmationSection
                            @t&c="viewTerms"
                            @on-continue="
                                submitSection(
                                    PageEnum.YourPlan,
                                    PageEnum.ConfirmationPage
                                )
                            "
                        />
                    </div>
                </div>
            </form>
        </template>
        <template #aside />
    </JourneyLayout>
</template>

<style scoped>
.your-plan-page {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
</style>
